import React from "react";
import { Box } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";

const flow = keyframes`
    from { stroke-dashoffset: 0; }
    to { stroke-dashoffset: -20; }
`;

const pulse = keyframes`
    0%, 100% { opacity: 0.55; transform: scale(1); }
    50%      { opacity: 0.15; transform: scale(1.6); }
`;

const drift = keyframes`
    0%   { transform: translate(0, 0); }
    50%  { transform: translate(8px, -6px); }
    100% { transform: translate(0, 0); }
`;

const NODES = [
    { x: 80, y: 140, r: 3 },
    { x: 240, y: 90, r: 5 },
    { x: 410, y: 180, r: 3 },
    { x: 580, y: 110, r: 7 },
    { x: 760, y: 220, r: 4 },
    { x: 920, y: 140, r: 5 },
    { x: 1080, y: 230, r: 3 },
    { x: 160, y: 360, r: 4 },
    { x: 360, y: 440, r: 7 },
    { x: 540, y: 360, r: 4 },
    { x: 720, y: 480, r: 5 },
    { x: 900, y: 420, r: 4 },
    { x: 1090, y: 480, r: 4 },
    { x: 110, y: 610, r: 3 },
    { x: 320, y: 680, r: 5 },
    { x: 540, y: 640, r: 3 },
    { x: 760, y: 700, r: 4 },
    { x: 980, y: 640, r: 5 },
];

const EDGES = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6],
    [3, 9], [9, 8], [8, 7], [9, 10], [10, 11], [11, 12], [11, 5],
    [7, 13], [8, 14], [10, 15], [10, 16], [11, 17], [15, 16], [16, 17], [12, 17],
    [0, 7], [2, 8], [6, 12], [13, 14], [14, 15],
];

const ICON_BOX_PATH = "M-7,-5 L7,-5 L7,5 L-7,5 Z M-7,-1 L7,-1";
const ICON_TRUCK_PATH = "M-8,-4 L4,-4 L4,4 L-8,4 Z M4,-1 L8,-1 L8,4 L4,4 Z";
const ICON_HUB_PATH = "M0,-7 L6,0 L0,7 L-6,0 Z";

const HUBS = [3, 8, 11];
const TRUCKS = [1, 14];
const BOXES = [5, 10, 17];

const SupplyChainBackdrop = ({ opacity = 0.55, tone = "default", animate = false }) => {
    const theme = useTheme();

    const palette = tone === "onPrimary"
        ? {
              stroke: "rgba(255,255,255,0.35)",
              node: "rgba(255,255,255,0.7)",
              hub: "rgba(255,255,255,0.95)",
              accent: "rgba(255,255,255,0.6)",
              hubGlow: "rgba(255,255,255,0.18)",
          }
        : {
              stroke: alpha(theme.palette.primary.main, 0.35),
              node: alpha(theme.palette.primary.main, 0.55),
              hub: alpha(theme.palette.secondary.main, 0.7),
              accent: alpha(theme.palette.secondary.main, 0.5),
              hubGlow: alpha(theme.palette.secondary.main, 0.18),
          };

    const { stroke, node, hub, accent, hubGlow } = palette;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                opacity,
                zIndex: 0,
                ...(animate && {
                    "& .scm-edge": {
                        animation: `${flow} 1.6s linear infinite`,
                    },
                    "& .scm-hub-glow": {
                        transformOrigin: "center",
                        transformBox: "fill-box",
                        animation: `${pulse} 2.4s ease-in-out infinite`,
                    },
                    "& .scm-net": {
                        animation: `${drift} 6s ease-in-out infinite`,
                    },
                }),
            }}
        >
            <svg
                viewBox="0 0 1200 800"
                preserveAspectRatio="xMidYMid slice"
                width="100%"
                height="100%"
            >
                <g className="scm-net">
                <g fill="none" stroke={stroke} strokeWidth={1} strokeLinecap="round">
                    {EDGES.map(([a, b], i) => {
                        const n1 = NODES[a];
                        const n2 = NODES[b];
                        const dashed = animate || i % 3 === 0;
                        return (
                            <line
                                key={`e-${i}`}
                                className="scm-edge"
                                x1={n1.x}
                                y1={n1.y}
                                x2={n2.x}
                                y2={n2.y}
                                strokeDasharray={dashed ? "4 6" : undefined}
                            />
                        );
                    })}
                </g>

                <g>
                    {NODES.map((n, i) => {
                        if (HUBS.includes(i)) {
                            return (
                                <g key={`n-${i}`} transform={`translate(${n.x} ${n.y})`}>
                                    <circle r={n.r + 8} className="scm-hub-glow" fill={hubGlow} />
                                    <path d={ICON_HUB_PATH} fill={hub} />
                                </g>
                            );
                        }
                        if (TRUCKS.includes(i)) {
                            return (
                                <g key={`n-${i}`} transform={`translate(${n.x} ${n.y})`}>
                                    <path
                                        d={ICON_TRUCK_PATH}
                                        fill="none"
                                        stroke={accent}
                                        strokeWidth={1.2}
                                        strokeLinejoin="round"
                                    />
                                </g>
                            );
                        }
                        if (BOXES.includes(i)) {
                            return (
                                <g key={`n-${i}`} transform={`translate(${n.x} ${n.y})`}>
                                    <path
                                        d={ICON_BOX_PATH}
                                        fill="none"
                                        stroke={node}
                                        strokeWidth={1.2}
                                        strokeLinejoin="round"
                                    />
                                </g>
                            );
                        }
                        return (
                            <circle
                                key={`n-${i}`}
                                cx={n.x}
                                cy={n.y}
                                r={n.r}
                                fill={node}
                            />
                        );
                    })}
                </g>
                </g>
            </svg>
        </Box>
    );
};

export default SupplyChainBackdrop;